import { DateTime } from 'luxon';
import { ANALYTICS_LATENCY_BUCKETS_MS, IST_ZONE, type AnalyticsDto, type AnalyticsGrain } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE USAGE ANALYTICS REPORT, READ. [ANALYTICS §5]
 *
 *  The pure half of the page: every number a sentence states is derived here, once, and the
 *  words live in `pages/UsageAnalytics/copy.ts`. No derivation re-ranks or re-sorts what the
 *  server sent — `RankedList`'s rule, because a client sort over a top-N orders N rows and lies
 *  about the rest.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const MINUTE_MS = 60_000;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ A CAPTURE GAP IS `expected − captured` LESS THE ROLLUP'S LAG — AND THE LAG IS BOUNDED.
 *
 *  `minutesExpected` counts every minute from the window's start through the minute holding
 *  `generatedAt`, and a rolled-up window's newest row ends a few minutes before that: a minute is
 *  rolled once it ended ninety seconds ago, by a tick that runs once a minute. Read raw, every
 *  1d–90d report would say "99% of minutes were captured" on every load — a warning that fires
 *  always is a warning nobody reads — so the minutes after `dataThrough` are allowed as lag.
 *
 *  ⚠️ BUT ONLY `ROLLUP_LAG_MINUTES` OF THEM. `dataThrough` is the end of the newest row the read
 *  found, so it STOPS when the rollup stops: an uncapped allowance would call a worker that has
 *  been down for three days "not rolled yet", drop the gap sentence, and let the usage-hours grid
 *  average in dates that hold nothing — the quiet-minutes reading `coverage` exists to prevent.
 *
 *  ⚠️ A REPORT WHOSE WINDOW COULD NOT BE READ HAS NO COVERAGE AT ALL. The server answers a failed
 *  load with every section `UNAVAILABLE` and `{minutesCaptured: 0, dataThrough: null}` — the same
 *  numbers as a window that genuinely captured nothing. Read as a gap it would say "0% of minutes
 *  were captured" about a record that may be whole and simply unreachable, so it is `unread`.
 *  ⚠️ THE WIRE HAS NO FIELD FOR THIS: the five failures together are the only discriminator, and a
 *  window that loaded and then failed all five sections independently reads as unread too.
 *
 *  ⚠️ THE PERCENTAGE IS FLOORED, SO IT NEVER READS 100 WHILE A MINUTE IS MISSING. Rounded, 9,999
 *  of 10,000 minutes is "100% of minutes were captured" — a sentence that exists only when
 *  that is false.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * The newest rolled minute on a healthy server ends at most FOUR whole minutes before the end of
 * the minute holding `generatedAt`: the ninety-second lag, the minute boundary it floors to, and
 * the one-minute tick the default `ANALYTICS_ROLLUP_TICK_MS` sets. One more for a tick that runs
 * late or takes its time. ⚠️ A server configured with a slower tick shows its tail as a gap — the
 * page does not receive the setting, and an allowance wide enough for fifteen minutes would hide a
 * stalled rollup for fifteen minutes on every server that did not ask for one.
 */
export const ROLLUP_LAG_MINUTES = 5;

const REPORT_SECTIONS = ['overview', 'usageHours', 'geography', 'apiHealth', 'security'] as const;

/** What coverage is read from. Each section only as `{ data } | { failure }`, which is all it needs to know. */
export type CoverageInput = Pick<AnalyticsDto, 'coverage' | 'generatedAt'> &
  Record<(typeof REPORT_SECTIONS)[number], { data: unknown } | { failure: string }>;

export type CoverageReading =
  | { kind: 'unread' }
  | {
      kind: 'read';
      /** Minutes the window should hold, less the lag allowance. */
      due: number;
      captured: number;
      /** Whole percent captured, floored; `null` when nothing is missing. */
      gapPercent: number | null;
    };

export const readCoverage = (report: CoverageInput): CoverageReading => {
  const { minutesExpected, minutesCaptured, dataThrough } = report.coverage;
  if (
    dataThrough === null &&
    minutesCaptured === 0 &&
    REPORT_SECTIONS.every((section) => 'failure' in report[section])
  ) {
    return { kind: 'unread' };
  }
  const endOfExpected = Math.floor(Date.parse(report.generatedAt) / MINUTE_MS) * MINUTE_MS + MINUTE_MS;
  const behind =
    dataThrough === null ? 0 : Math.max(0, Math.round((endOfExpected - Date.parse(dataThrough)) / MINUTE_MS));
  const due = Math.max(0, minutesExpected - Math.min(behind, ROLLUP_LAG_MINUTES));
  if (minutesCaptured >= due) return { kind: 'read', due, captured: minutesCaptured, gapPercent: null };
  return { kind: 'read', due, captured: minutesCaptured, gapPercent: Math.floor((minutesCaptured / due) * 100) };
};

/**
 * Whether the window's record is whole — read, and with no gap. The usage-hours grid averages
 * only then, because an average divides by dates a gap would count as quiet.
 */
export const isWholeWindow = (report: CoverageInput): boolean => {
  const coverage = readCoverage(report);
  return coverage.kind === 'read' && coverage.gapPercent === null;
};

/** `HH:mm`, in IST. The page never shows a device-local time. */
export const istTime = (instant: string): string => DateTime.fromISO(instant, { zone: IST_ZONE }).toFormat('HH:mm');

/** `20 Aug 2026, 14:05`, in IST. */
export const istDateTime = (instant: string): string =>
  DateTime.fromISO(instant, { zone: IST_ZONE }).toFormat('d LLL yyyy, HH:mm');

/**
 * `HH:mm` when `instant` falls on the IST date of `reference`, and the date with it otherwise.
 * ⚠️ A BARE TIME IS A CLAIM ABOUT TODAY: "data through 14:03" from a rollup that stopped three days
 * ago reads as a record that is current.
 */
export const istTimeOrDateTime = (instant: string, reference: string): string => {
  const at = DateTime.fromISO(instant, { zone: IST_ZONE });
  return at.hasSame(DateTime.fromISO(reference, { zone: IST_ZONE }), 'day') ? istTime(instant) : istDateTime(instant);
};

/**
 * A series point's label at its own grain: `14:05` for a minute, `20 Aug, 14:30` for an IST
 * hour (which starts at :30 UTC — luxon's zone does that arithmetic), `20 Aug` for an IST day.
 */
export const seriesPointLabel = (instant: string, grain: AnalyticsGrain): string => {
  const at = DateTime.fromISO(instant, { zone: IST_ZONE });
  return grain === 'minute'
    ? at.toFormat('HH:mm')
    : grain === 'hour'
      ? at.toFormat('d LLL, HH:mm')
      : at.toFormat('d LLL');
};

/**
 * ⚠️ TWO SEGMENT STRENGTHS, AS LITERAL CLASSES, BOTTOM SEGMENT FIRST. The same
 * interpolation trap as `HEAT_LEVEL_CLASSES`, and the same reason for `foreground` rather than
 * `data-bar`: two stacked series must be told apart by LIGHTNESS alone (R1), and a wash that is
 * already 8–12% alpha has no room for a second step. `theme/analyticsTint.test.ts` measures the
 * pair against the card and against each other.
 */
export const SERIES_SEGMENT_CLASSES = ['bg-foreground/60', 'bg-foreground/25'] as const;

export type SeriesPoint = { at: string; values: readonly number[] };

/**
 * Each point's segments as percentages of the TALLEST STACK, so the highest bar fills the plot
 * and a series of zeros draws nothing rather than dividing by zero.
 */
export const seriesHeights = (points: readonly SeriesPoint[]): number[][] => {
  const tallest = points.reduce((highest, point) => Math.max(highest, sum(point.values)), 0);
  return points.map((point) => point.values.map((value) => (tallest > 0 ? (value / tallest) * 100 : 0)));
};

const sum = (values: readonly number[]): number => values.reduce((total, value) => total + value, 0);

/** The point with the most in it, all segments together; the earliest on a tie; `null` if every point is zero. */
export const seriesPeak = (points: readonly SeriesPoint[]): { at: string; total: number } | null => {
  let peak: { at: string; total: number } | null = null;
  for (const point of points) {
    const total = sum(point.values);
    if (total > 0 && (peak === null || total > peak.total)) peak = { at: point.at, total };
  }
  return peak;
};

/** Every segment of every point, per series. */
export const seriesTotals = (points: readonly SeriesPoint[], seriesCount: number): number[] =>
  Array.from({ length: seriesCount }, (_, index) =>
    points.reduce((total, point) => total + (point.values[index] ?? 0), 0),
  );

/**
 * ⚠️ THE LAST FINITE LATENCY BOUND, READ FROM THE STORED TUPLE. A percentile that fell in the
 * unbounded top bucket arrives as this number with `overflow`, and the page says "> 10 s" —
 * the bucket holds requests SLOWER than the bound, so "at least" would be one millisecond wrong
 * and "10,000 ms" would be a measurement nobody took.
 */
export const LATENCY_CEILING_MS = Math.max(...ANALYTICS_LATENCY_BUCKETS_MS);

export type LatencyText = { kind: 'none' } | { kind: 'overflow'; seconds: number } | { kind: 'ms'; ms: number };

/**
 * ⚠️ THE WIRE CARRIES ONE `overflow` FLAG, AND IT IS THE 95th PERCENTILE'S. A median can only
 * overflow when the 95th has too, and it then arrives as exactly the ceiling — so `p50` reads
 * "> 10 s" only when the flag is set AND it sits on the ceiling. Interpolation cannot land on the
 * ceiling from below except at a bucket's exact edge, which is the one reading this could
 * overstate by a millisecond.
 */
export const latencyText = (ms: number | null, overflow: boolean): LatencyText => {
  if (ms === null) return { kind: 'none' };
  if (overflow && ms >= LATENCY_CEILING_MS) return { kind: 'overflow', seconds: LATENCY_CEILING_MS / 1000 };
  return { kind: 'ms', ms: Math.round(ms) };
};

/** `City, State, Country`, skipping what the geo database did not know. */
export const placeLabel = (place: { city: string | null; state: string | null; country: string | null }): string =>
  [place.city, place.state, place.country].filter((part): part is string => part !== null && part !== '').join(', ');

/** Integers in the Indian grouping the rest of the app uses. */
export const formatCount = (value: number): string => value.toLocaleString('en-IN');
